'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { collection, query, getDocs, limit, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS, Document as WikiDocument } from '@/types/firestore';
import { searchKorean } from '@/lib/utils/korean';
import { useSemanticSearch } from '@/hooks/useSemanticSearch';

import SearchInput from './SearchInput';
import NormalResults from './NormalResults';
import AIResults from './AIResults';
import AIEmptyState from './AIEmptyState';
import SearchFooter from './SearchFooter';
import InsurancePDFQuery from './InsurancePDFQuery';
import SearchSummaryCard from './SearchSummaryCard';

export default function SearchModal() {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [results, setResults] = useState<WikiDocument[]>([]);
    const [allDocs, setAllDocs] = useState<WikiDocument[]>([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [searchMode, setSearchMode] = useState<'normal' | 'ai' | 'insurance'>('normal');
    const [apiKey, setApiKey] = useState<string | null>(null);
    // CL-7: 과거 약관 버전 조회용
    const [targetDate, setTargetDate] = useState<string | null>(null);

    const router = useRouter();
    const modalRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { performSearch, isSearching, answer, sources, error: aiError } = useSemanticSearch({ apiKey });

    // Fetch API Key
    useEffect(() => {
        const fetchKey = async () => {
            if (!user) return;
            try {
                const token = await user.getIdToken();
                const res = await fetch('/api/ai/settings', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setApiKey(data.geminiApiKey);
                }
            } catch (err) {
                console.error("SearchModal: Error fetching API Key", err);
            }
        };
        fetchKey();
    }, [user]);

    // 단축키 핸들러 (Cmd+K, Ctrl+K)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen(prev => !prev);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // 커스텀 이벤트 핸들러 (외부 버튼에서 열기 용)
    useEffect(() => {
        const handleOpenSearch = () => setIsOpen(true);
        window.addEventListener('open-search', handleOpenSearch);
        return () => window.removeEventListener('open-search', handleOpenSearch);
    }, []);

    // 모달이 열릴 때 자동 포커스
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
            setSelectedIndex(0);
        } else {
            setSearchQuery('');
            setDebouncedQuery('');
            setResults([]);
            setSearchMode('normal');
        }
    }, [isOpen]);

    // 전체 문서 로드 (모달 열릴 때 1회)
    useEffect(() => {
        if (isOpen && !isDataLoaded && user) {
            const loadAllDocs = async () => {
                setLoading(true);
                try {
                    const docsRef = collection(db, COLLECTIONS.DOCUMENTS);
                    const q = query(docsRef, orderBy('updatedAt', 'desc'), limit(500));
                    const snapshot = await getDocs(q);
                    const docs = snapshot.docs.map(doc => ({
                        ...doc.data(),
                        id: doc.id
                    })) as WikiDocument[];

                    const accessibleDocs = docs.filter(doc =>
                        (doc.visibility === 'public' || doc.authorId === user.uid) &&
                        !doc.isDeleted
                    );

                    setAllDocs(accessibleDocs);
                    setIsDataLoaded(true);
                } catch (error) {
                    console.error("Failed to load documents for search:", error);
                } finally {
                    setLoading(false);
                }
            };
            loadAllDocs();
        }
    }, [isOpen, isDataLoaded, user]);

    // S2: 디바운스된 검색어로 필터링
    useEffect(() => {
        if (searchMode === 'ai') return;
        if (!debouncedQuery.trim()) {
            setResults([]);
            return;
        }

        const q = debouncedQuery.toLowerCase();
        const filtered = allDocs.filter(doc => {
            const titleMatch = searchKorean(doc.title, debouncedQuery);
            const contentMatch = doc.content?.toLowerCase().includes(q);
            return titleMatch || contentMatch;
        });

        const sorted = filtered.sort((a, b) => {
            const aTitleMatch = searchKorean(a.title, debouncedQuery);
            const bTitleMatch = searchKorean(b.title, debouncedQuery);
            if (aTitleMatch && !bTitleMatch) return -1;
            if (!aTitleMatch && bTitleMatch) return 1;
            return (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0);
        }).slice(0, 20);

        setResults(sorted);
    }, [debouncedQuery, allDocs, searchMode]);

    // 키보드 네비게이션
    const handleListKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        } else if (e.key === 'Enter') {
            if (searchMode === 'ai' && searchQuery.trim()) {
                performSearch(searchQuery);
            } else if (results[selectedIndex]) {
                handleSelect(results[selectedIndex].id);
            }
        }
    };

    const handleSelect = useCallback((docId: string) => {
        router.push(`/docs/${docId}`);
        setIsOpen(false);
    }, [router]);

    const handleDebouncedQuery = useCallback((query: string) => {
        setDebouncedQuery(query);
    }, []);

    // S3: 추천 질문 클릭 핸들러
    const handleQuestionClick = useCallback((question: string) => {
        setSearchQuery(question);
        performSearch(question);
    }, [performSearch]);

    if (!isOpen) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="검색"
            className="fixed inset-0 z-[100] flex items-start justify-center pt-0 sm:pt-[12vh] px-0 sm:px-4"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
                onClick={() => setIsOpen(false)}
            />

            {/* Modal Content */}
            <div
                ref={modalRef}
                className="relative w-full h-full sm:h-auto sm:max-w-2xl bg-white sm:rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-white/20 flex flex-col"
                onKeyDown={handleListKeyDown}
            >
                {/* 검색 입력 */}
                <SearchInput
                    searchMode={searchMode}
                    setSearchMode={setSearchMode}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onDebouncedQuery={handleDebouncedQuery}
                    onAISearch={performSearch}
                    isSearching={isSearching}
                    apiKey={apiKey}
                    inputRef={inputRef}
                    targetDate={targetDate}
                    setTargetDate={setTargetDate}
                />

                {/* 결과 영역 */}
                <div className="max-h-[calc(100vh-200px)] sm:max-h-[60vh] overflow-y-auto overflow-x-hidden p-2 z-0 relative scroll-smooth">
                    {/* 일반 검색 결과 */}
                    {searchMode === 'normal' && (
                        <>
                            <NormalResults
                                loading={loading}
                                results={results}
                                searchQuery={searchQuery}
                                selectedIndex={selectedIndex}
                                onSelect={handleSelect}
                            />
                            <SearchSummaryCard
                                query={searchQuery}
                                results={results.map(r => ({ id: r.id, title: r.title, snippet: r.content?.slice(0, 200) }))}
                                onSelect={handleSelect}
                            />
                        </>
                    )}

                    {/* AI 검색 결과 */}
                    {searchMode === 'ai' && (
                        <div className="p-4 space-y-6">
                            <AIResults
                                isSearching={isSearching}
                                answer={answer}
                                sources={sources}
                                aiError={aiError}
                                searchQuery={searchQuery}
                                onSelect={handleSelect}
                                onRetry={performSearch}
                            />

                            {/* Empty State (검색 전) */}
                            {!isSearching && !answer && !aiError && (
                                <AIEmptyState
                                    apiKey={apiKey}
                                    onQuestionClick={handleQuestionClick}
                                />
                            )}
                        </div>
                    )}

                    {/* 보험 약관 PDF 질의 */}
                    {searchMode === 'insurance' && (
                        <InsurancePDFQuery />
                    )}
                </div>

                {/* 하단 도움말 */}
                <SearchFooter />
            </div>
        </div>
    );
}
